import { MAX_INVESTMENT_AMOUNT_USD, MIN_INVESTMENT_AMOUNT } from '../constants/profileFieldLimits';

/** EUR-based fallback if getExchangeRates is down. Same shape as the Cloud Function. */
export const FALLBACK_EUR_RATES: Record<string, number> = {
  eur: 1,
  usd: 1.08,
  gbp: 0.86,
  sek: 11.2,
};

export function convertAmountBetweenCurrencies(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  rates: Record<string, number>
): number | null {
  const from = fromCurrency.toLowerCase();
  const to = toCurrency.toLowerCase();
  if (from === to) return amount;
  const fromRate = rates[from];
  const toRate = rates[to];
  if (fromRate == null || toRate == null || fromRate <= 0 || toRate <= 0) return null;
  return amount * (toRate / fromRate);
}

function ratesOrFallback(rates: Record<string, number> | null | undefined): Record<string, number> {
  return rates && Object.keys(rates).length > 0 ? rates : FALLBACK_EUR_RATES;
}

/** Minimum startup ask in local currency (USD floor is $50,000). */
export function minStartupAskInCurrency(
  currency: string,
  rates: Record<string, number> | null | undefined
): number {
  const converted = convertAmountBetweenCurrencies(
    MIN_INVESTMENT_AMOUNT,
    'usd',
    currency,
    ratesOrFallback(rates)
  );
  if (converted == null || !Number.isFinite(converted)) return MIN_INVESTMENT_AMOUNT;
  return Math.ceil(converted / 1000) * 1000;
}

/** Maximum investment/ask in local currency (USD ceiling, default 200M). */
export function maxInvestmentInCurrency(
  currency: string,
  rates: Record<string, number> | null | undefined,
  maxUsd: number = MAX_INVESTMENT_AMOUNT_USD
): number {
  const converted = convertAmountBetweenCurrencies(maxUsd, 'usd', currency, ratesOrFallback(rates));
  if (converted == null || !Number.isFinite(converted)) return maxUsd;
  return Math.floor(converted / 1000) * 1000;
}

export function startupAskMeetsUsdMinimum(
  amount: number,
  currency: string,
  rates: Record<string, number> | null | undefined
): boolean {
  if (!Number.isFinite(amount) || amount < 0) return false;
  const usd = convertAmountBetweenCurrencies(amount, currency, 'usd', ratesOrFallback(rates));
  if (usd == null || !Number.isFinite(usd)) return amount >= MIN_INVESTMENT_AMOUNT;
  return usd >= MIN_INVESTMENT_AMOUNT - 0.5;
}
